import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {catchError, concatMap, filter, map, of, switchMap, withLatestFrom} from 'rxjs';
import {tap} from 'rxjs/operators';
import {SeFeToastService} from 'se-fe-toast';
import {productQueryParamsToListBundlesParams} from '../../../models';
import {Bundle} from '../../../models/clare';
import {BundleApiService} from '../../../services/bundle/bundle-api.service';
import {CatalogApiService} from '../../../services/catalog-api.service';
import {CatalogMembershipApiService} from '../../../services/catalog-membership-api.service';
import {ProductApiService} from '../../../services/product/product-api.service';
import {RegistrationApiService} from '../../../services/registration-api.service';
import {selectSelectedOrganizationId} from '../../../state/router/router.selectors';
import {emptyPagedListResponse, isActiveListSearch, isNotFoundError} from '../../../utils/list-page-utils';
import {CatalogDetailActions} from './catalog-detail.actions';
import {
  selectCatalogBundlesLoaded,
  selectCatalogBundlesLoading,
  selectCatalogBundlesQuery,
  selectCatalogBundleSelection,
  selectCatalogBundleSelectionQuery,
  selectCatalogDetail,
  selectCatalogProductSelection,
  selectCatalogProductSelectionQuery,
  selectCatalogProductsQuery,
} from './catalog-detail.selectors';

const LOAD_CATALOG_REGISTRATIONS_ERROR_MESSAGE = 'Failed to load catalog registrations, please refresh and try again.';

@Injectable()
export class CatalogDetailEffects {
  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.load),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      switchMap(([{catalogId}, organizationId]) => {
        if (!organizationId) {
          return of(
            CatalogDetailActions.loadFailure({
              error: {message: 'Failed to load the catalog details, please refresh the page.'},
            })
          );
        }

        return this.catalogApi.getById(organizationId, catalogId).pipe(
          map(catalog => CatalogDetailActions.loadSuccess({catalog})),
          catchError(() =>
            of(
              CatalogDetailActions.loadFailure({
                error: {message: 'Failed to load the catalog details, please refresh the page.'},
              })
            )
          )
        );
      })
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.update),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      concatMap(([{catalog}, organizationId]) => {
        if (!organizationId) {
          return of(
            CatalogDetailActions.updateFailure({
              error: {message: 'Failed to update the catalog, please refresh the page and try again.'},
            })
          );
        }

        return this.catalogApi.update(catalog, organizationId).pipe(
          map(updatedCatalog => CatalogDetailActions.updateSuccess({catalog: updatedCatalog})),
          catchError(() =>
            of(
              CatalogDetailActions.updateFailure({
                error: {message: 'Failed to update the catalog, please refresh the page and try again.'},
              })
            )
          )
        );
      })
    )
  );

  loadProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.loadProducts),
      withLatestFrom(this.store.select(selectCatalogProductsQuery), this.store.select(selectCatalogDetail)),
      filter(([, , catalog]) => catalog?.id !== undefined),
      switchMap(([, queryParams, catalog]) =>
        this.productApi.getProductsByCatalog(Number(catalog!.id), queryParams).pipe(
          map(products => CatalogDetailActions.loadProductsSuccess({products})),
          catchError(error => {
            if (isActiveListSearch(queryParams) && isNotFoundError(error)) {
              return of(
                CatalogDetailActions.loadProductsSuccess({
                  products: emptyPagedListResponse(queryParams, 'Items::Product'),
                })
              );
            }

            return of(
              CatalogDetailActions.loadProductsFailure({
                error: {message: 'Failed to load catalog products, please refresh and try again.'},
              })
            );
          })
        )
      )
    )
  );

  loadRegistrationsAfterCatalogLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.loadSuccess),
      map(() => CatalogDetailActions.loadRegistrations())
    )
  );

  loadRegistrations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.loadRegistrations),
      withLatestFrom(this.store.select(selectSelectedOrganizationId), this.store.select(selectCatalogDetail)),
      switchMap(([, organizationId, catalog]) => {
        if (!organizationId || !catalog?.id) {
          return of(
            CatalogDetailActions.loadRegistrationsFailure({
              error: {message: LOAD_CATALOG_REGISTRATIONS_ERROR_MESSAGE},
            })
          );
        }

        const catalogKey$ = catalog.key
          ? of(catalog.key)
          : this.catalogApi
              .getAllCatalogsByOrganizationId(organizationId)
              .pipe(map(catalogs => catalogs.find(({id}) => id === catalog.id)?.key));

        return catalogKey$.pipe(
          switchMap(catalogKey => {
            if (!catalogKey) {
              return of(
                CatalogDetailActions.loadRegistrationsFailure({
                  error: {message: LOAD_CATALOG_REGISTRATIONS_ERROR_MESSAGE},
                })
              );
            }

            return this.registrationApi
              .getByCatalogKey(organizationId, catalogKey)
              .pipe(map(registrations => CatalogDetailActions.loadRegistrationsSuccess({registrations})));
          }),
          catchError(() =>
            of(
              CatalogDetailActions.loadRegistrationsFailure({
                error: {message: LOAD_CATALOG_REGISTRATIONS_ERROR_MESSAGE},
              })
            )
          )
        );
      })
    )
  );

  loadProductsAfterCatalogLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.loadSuccess),
      map(() => CatalogDetailActions.loadProducts())
    )
  );

  reloadProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.productsQueryChanged),
      map(() => CatalogDetailActions.loadProducts())
    )
  );

  removeProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.removeProducts),
      withLatestFrom(this.store.select(selectCatalogDetail)),
      concatMap(([{productIds}, catalog]) => {
        const catalogId = Number(catalog!.id);

        return this.catalogMembershipApi.remove({catalog_id: catalogId, product_ids: productIds}).pipe(
          map(() => CatalogDetailActions.removeProductsSuccess({productIds})),
          catchError(() =>
            of(
              CatalogDetailActions.removeProductsFailure({
                error: {message: 'Failed to remove products from the catalog, please refresh and try again.'},
              })
            )
          )
        );
      })
    )
  );

  reloadProductsAfterRemove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.removeProductsSuccess),
      map(() => CatalogDetailActions.loadProducts())
    )
  );

  removeProductsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogDetailActions.removeProductsSuccess),
        tap(() => this.toast.done('Successfully removed products from catalog'))
      ),
    {dispatch: false}
  );

  removeProductsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogDetailActions.removeProductsFailure),
        tap(({error}) => this.toast.critical(error.message))
      ),
    {dispatch: false}
  );

  ensureBundlesLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.ensureBundlesLoaded),
      withLatestFrom(this.store.select(selectCatalogBundlesLoaded), this.store.select(selectCatalogBundlesLoading)),
      filter(([, loaded, loading]) => !loaded && !loading),
      map(() => CatalogDetailActions.loadBundles())
    )
  );

  loadBundles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.loadBundles),
      withLatestFrom(this.store.select(selectCatalogBundlesQuery), this.store.select(selectCatalogDetail)),
      filter(([, , catalog]) => catalog?.id !== undefined),
      switchMap(([, queryParams, catalog]) =>
        this.bundleApi.listPaged(productQueryParamsToListBundlesParams(queryParams, Number(catalog!.id))).pipe(
          map(bundles => CatalogDetailActions.loadBundlesSuccess({bundles})),
          catchError(error => {
            if (isActiveListSearch(queryParams) && isNotFoundError(error)) {
              return of(
                CatalogDetailActions.loadBundlesSuccess({
                  bundles: emptyPagedListResponse<Bundle>(queryParams, 'Items::Bundle'),
                })
              );
            }

            return of(
              CatalogDetailActions.loadBundlesFailure({
                error: {message: 'Failed to load catalog bundles, please refresh and try again.'},
              })
            );
          })
        )
      )
    )
  );

  reloadBundles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.bundlesQueryChanged),
      map(() => CatalogDetailActions.loadBundles())
    )
  );

  removeBundles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.removeBundles),
      withLatestFrom(this.store.select(selectCatalogDetail)),
      concatMap(([{bundleIds}, catalog]) => {
        const catalogId = Number(catalog!.id);

        return this.catalogMembershipApi.remove({catalog_id: catalogId, bundle_ids: bundleIds}).pipe(
          map(() => CatalogDetailActions.removeBundlesSuccess({bundleIds})),
          catchError(() =>
            of(
              CatalogDetailActions.removeBundlesFailure({
                error: {message: 'Failed to remove bundles from the catalog, please refresh and try again.'},
              })
            )
          )
        );
      })
    )
  );

  reloadBundlesAfterRemove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.removeBundlesSuccess),
      map(() => CatalogDetailActions.loadBundles())
    )
  );

  removeBundlesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogDetailActions.removeBundlesSuccess),
        tap(() => this.toast.done('Successfully removed bundles from catalog'))
      ),
    {dispatch: false}
  );

  removeBundlesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogDetailActions.removeBundlesFailure),
        tap(({error}) => this.toast.critical(error.message))
      ),
    {dispatch: false}
  );

  openProductChooser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.openProductChooser),
      map(() => CatalogDetailActions.loadProductChoices())
    )
  );

  loadProductChoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.loadProductChoices),
      withLatestFrom(
        this.store.select(selectCatalogProductSelectionQuery),
        this.store.select(selectSelectedOrganizationId),
        this.store.select(selectCatalogDetail)
      ),
      switchMap(([, queryParams, organizationId, catalog]) => {
        if (!organizationId) {
          return of(
            CatalogDetailActions.loadProductChoicesFailure({
              error: {message: 'Failed to load products, please refresh and try again.'},
            })
          );
        }

        const productChoicesQuery = {...queryParams, exclude_catalog_id: Number(catalog!.id)};

        return this.productApi.getProductsByOrganization(organizationId, productChoicesQuery).pipe(
          map(products => CatalogDetailActions.loadProductChoicesSuccess({products})),
          catchError(error => {
            if (isActiveListSearch(queryParams) && isNotFoundError(error)) {
              return of(
                CatalogDetailActions.loadProductChoicesSuccess({
                  products: emptyPagedListResponse(queryParams, 'Items::Product'),
                })
              );
            }

            return of(
              CatalogDetailActions.loadProductChoicesFailure({
                error: {message: 'Failed to load products, please refresh and try again.'},
              })
            );
          })
        );
      })
    )
  );

  reloadProductChoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.productChoicesQueryChanged),
      map(() => CatalogDetailActions.loadProductChoices())
    )
  );

  addSelectedProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.addSelectedProducts),
      withLatestFrom(this.store.select(selectCatalogProductSelection), this.store.select(selectCatalogDetail)),
      concatMap(([, productSelection, catalog]) => {
        const catalogId = Number(catalog?.id);
        const productIds = productSelection.selectedProductIds.map(Number).filter(Number.isFinite);

        if (!catalog?.id || !Number.isFinite(catalogId) || productIds.length === 0) {
          return of(
            CatalogDetailActions.addSelectedProductsFailure({
              error: {message: 'Failed to add products to the catalog, please refresh and try again.'},
            })
          );
        }

        return this.catalogMembershipApi.assign({catalog_id: catalogId, product_ids: productIds}).pipe(
          map(() => CatalogDetailActions.addSelectedProductsSuccess()),
          catchError(() =>
            of(
              CatalogDetailActions.addSelectedProductsFailure({
                error: {message: 'Failed to add products to the catalog, please refresh and try again.'},
              })
            )
          )
        );
      })
    )
  );

  reloadProductsAfterAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.addSelectedProductsSuccess),
      map(() => CatalogDetailActions.loadProducts())
    )
  );

  addSelectedProductsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogDetailActions.addSelectedProductsSuccess),
        tap(() => this.toast.done('Successfully added products to catalog'))
      ),
    {dispatch: false}
  );

  openBundleChooser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.openBundleChooser),
      map(() => CatalogDetailActions.loadBundleChoices())
    )
  );

  loadBundleChoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.loadBundleChoices),
      withLatestFrom(
        this.store.select(selectCatalogBundleSelectionQuery),
        this.store.select(selectSelectedOrganizationId),
        this.store.select(selectCatalogDetail)
      ),
      switchMap(([, queryParams, organizationId, catalog]) => {
        const catalogId = Number(catalog?.id);
        if (!organizationId || !catalog?.id || !Number.isFinite(catalogId)) {
          return of(
            CatalogDetailActions.loadBundleChoicesFailure({
              error: {message: 'Failed to load bundles, please refresh and try again.'},
            })
          );
        }

        const bundleChoicesQuery = {...queryParams, exclude_catalog_id: catalogId};
        const bundleChoicesParams = {
          ...productQueryParamsToListBundlesParams(bundleChoicesQuery),
          organization_id: organizationId,
        };

        return this.bundleApi.listPaged(bundleChoicesParams).pipe(
          map(bundles => CatalogDetailActions.loadBundleChoicesSuccess({bundles})),
          catchError(error => {
            if (isActiveListSearch(queryParams) && isNotFoundError(error)) {
              return of(
                CatalogDetailActions.loadBundleChoicesSuccess({
                  bundles: emptyPagedListResponse<Bundle>(queryParams, 'Items::Bundle'),
                })
              );
            }

            return of(
              CatalogDetailActions.loadBundleChoicesFailure({
                error: {message: 'Failed to load bundles, please refresh and try again.'},
              })
            );
          })
        );
      })
    )
  );

  reloadBundleChoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.bundleChoicesQueryChanged),
      map(() => CatalogDetailActions.loadBundleChoices())
    )
  );

  addSelectedBundles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.addSelectedBundles),
      withLatestFrom(this.store.select(selectCatalogBundleSelection), this.store.select(selectCatalogDetail)),
      concatMap(([, bundleSelection, catalog]) => {
        const catalogId = Number(catalog?.id);
        const bundleIds = bundleSelection.selectedBundleIds.map(Number).filter(Number.isFinite);

        if (!catalog?.id || !Number.isFinite(catalogId) || bundleIds.length === 0) {
          return of(
            CatalogDetailActions.addSelectedBundlesFailure({
              error: {message: 'Failed to add bundles to the catalog, please refresh and try again.'},
            })
          );
        }

        return this.catalogMembershipApi.assign({catalog_id: catalogId, bundle_ids: bundleIds}).pipe(
          map(() => CatalogDetailActions.addSelectedBundlesSuccess()),
          catchError(() =>
            of(
              CatalogDetailActions.addSelectedBundlesFailure({
                error: {message: 'Failed to add bundles to the catalog, please refresh and try again.'},
              })
            )
          )
        );
      })
    )
  );

  reloadBundlesAfterAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogDetailActions.addSelectedBundlesSuccess),
      map(() => CatalogDetailActions.loadBundles())
    )
  );

  addSelectedBundlesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogDetailActions.addSelectedBundlesSuccess),
        tap(() => this.toast.done('Successfully added bundles to catalog'))
      ),
    {dispatch: false}
  );

  updateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogDetailActions.updateSuccess),
        tap(() => this.toast.done('Successfully updated catalog'))
      ),
    {dispatch: false}
  );

  updateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CatalogDetailActions.updateFailure),
        tap(({error}) => this.toast.critical(error.message))
      ),
    {dispatch: false}
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private catalogApi: CatalogApiService,
    private catalogMembershipApi: CatalogMembershipApiService,
    private productApi: ProductApiService,
    private bundleApi: BundleApiService,
    private registrationApi: RegistrationApiService,
    private toast: SeFeToastService
  ) {}
}
